import { useState, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, PackageMinus, RotateCcw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const CONSUMPTION_CATEGORIES = [
  "Boarding", "Grooming", "Veterinary / Clinic", "Daycare", "Training",
  "Cleaning / Hygiene", "Office / Administration", "Maintenance",
  "Staff Use", "Damaged / Wastage", "Expired", "Other",
];

const uuid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export function InternalConsumptionModal({ open, onOpenChange, products, onDone }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const tenantWide = !(user?.branch_ids && user.branch_ids.length);
  const { data: branches } = useQuery({
    queryKey: ["branches"],
    queryFn: async () => (await api.get("/branches")).data,
    enabled: tenantWide && open,
  });
  const [branchId, setBranchId] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ product_id: "", qty: 1 }]);
  // one idempotency key per logical submission; survives re-renders + network retries,
  // regenerated only after a terminal reset so a NEW consumption gets a NEW key.
  const idemRef = useRef(uuid());

  const avail = useMemo(() => Object.fromEntries((products || []).map((p) => [p.id, p])), [products]);
  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { product_id: "", qty: 1 }]);
  const rmLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));
  const reset = () => { setBranchId(""); setReason(""); setNotes(""); setLines([{ product_id: "", qty: 1 }]); idemRef.current = uuid(); };

  const noDup = new Set(lines.map((l) => l.product_id)).size === lines.length;
  const valid = reason && noDup && lines.every(
    (l) => l.product_id && Number(l.qty) > 0 && Number(l.qty) <= (avail[l.product_id]?.stock ?? 0));

  const submit = useMutation({
    mutationFn: () => api.post("/inventory/consumption", {
      branch_id: branchId || "", reason, notes,
      items: lines.map((l) => ({ product_id: l.product_id, qty: Number(l.qty) })),
    }, { headers: { "Idempotency-Key": idemRef.current } }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["consumption-log"] });
      toast.success(`Recorded ${res.data.number} — stock updated`);
      reset(); onOpenChange(false); onDone && onDone();
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="internal-consumption-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <PackageMinus className="h-5 w-5 text-slate-700" /> Internal Consumption
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-xs text-slate-500 -mt-2">Records stock used by the business. This is not a sale — no invoice, payment or customer charge is created.</DialogDescription>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {tenantWide && (
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Branch</Label>
                <Select value={branchId || "__all__"} onValueChange={(v) => setBranchId(v === "__all__" ? "" : v)}>
                  <SelectTrigger className="bg-white" data-testid="ic-branch"><SelectValue placeholder="Branch" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="__all__">Default</SelectItem>
                    {(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="bg-white" data-testid="ic-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {CONSUMPTION_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Items</Label>
            {lines.map((l, i) => {
              const p = avail[l.product_id];
              const stock = p?.stock ?? 0;
              const q = Number(l.qty) || 0;
              const over = l.product_id && q > stock;
              return (
                <div key={i} className="rounded-lg border border-slate-200 p-2" data-testid={`ic-line-${i}`}>
                  <div className="flex items-center gap-2">
                    <Select value={l.product_id} onValueChange={(v) => setLine(i, { product_id: v })}>
                      <SelectTrigger className="bg-white flex-1" data-testid={`ic-product-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                      <SelectContent className="bg-white max-h-64">
                        {(products || []).map((pr) => (
                          <SelectItem key={pr.id} value={pr.id} disabled={(pr.stock ?? 0) <= 0}>
                            {pr.name} — {pr.stock ?? 0}{pr.unit ? ` ${pr.unit}` : ""} left
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input type="number" min="0" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })}
                      className="w-24 bg-white text-right" data-testid={`ic-qty-${i}`} />
                    <button onClick={() => rmLine(i)} className="p-1 text-rose-500 hover:bg-rose-50 rounded" data-testid={`ic-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  {l.product_id && (
                    <div className="mt-1 flex items-center gap-2 text-xs" data-testid={`ic-impact-${i}`}>
                      <span className="text-slate-500">Available {stock}{p?.unit ? ` ${p.unit}` : ""}</span>
                      {q > 0 && !over && <span className="text-slate-700 flex items-center gap-1 font-medium">{stock} <ArrowRight className="h-3 w-3" /> {stock - q}</span>}
                      {over && <span className="text-rose-600 font-medium" data-testid={`ic-over-${i}`}>Exceeds available stock</span>}
                    </div>
                  )}
                </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={addLine} className="gap-1.5" data-testid="ic-add-line"><Plus className="h-4 w-4" /> Add product</Button>
          </div>

          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. used during morning bath round" className="bg-white" data-testid="ic-notes" />
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} data-testid="ic-cancel" className="min-h-[44px]">Cancel</Button>
          <Button onClick={() => submit.mutate()} disabled={!valid || submit.isPending} data-testid="ic-submit" className="min-h-[44px] bg-slate-900 hover:bg-slate-800 text-white">
            {submit.isPending ? "Recording…" : "Record Internal Consumption"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConsumptionLogModal({ open, onOpenChange, canConsume, onReversed }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["consumption-log", reason],
    queryFn: async () => (await api.get(`/inventory/consumption${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`)).data,
    enabled: open,
  });
  const reverse = useMutation({
    mutationFn: ({ id, why }) => api.post(`/inventory/consumption/${id}/reverse`, { reason: why }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["consumption-log"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      toast.success("Consumption reversed — stock restored");
      onReversed && onReversed();
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const doReverse = (r) => {
    const why = window.prompt(`Reason for reversing ${r.number}?`);
    if (why && why.trim()) reverse.mutate({ id: r.id, why: why.trim() });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="consumption-log-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Internal Consumption Log</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Internal stock usage records. Reverse an entry to restore stock.</DialogDescription>
        </DialogHeader>
        <div className="mb-3 w-56">
          <Select value={reason || "__all__"} onValueChange={(v) => setReason(v === "__all__" ? "" : v)}>
            <SelectTrigger className="bg-white" data-testid="log-filter-reason"><SelectValue placeholder="All reasons" /></SelectTrigger>
            <SelectContent className="bg-white max-h-64">
              <SelectItem value="__all__">All reasons</SelectItem>
              {CONSUMPTION_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {isLoading ? <p className="text-sm text-slate-500 py-6 text-center">Loading…</p> :
          !(data?.items || []).length ? <p className="text-sm text-slate-500 py-6 text-center" data-testid="log-empty">No internal consumption recorded yet.</p> : (
          <div className="space-y-2" data-testid="consumption-log-list">
            {data.items.map((r) => (
              <div key={r.id} className={`rounded-lg border p-3 ${r.kind === "reversal" ? "border-emerald-200 bg-emerald-50/40" : r.status === "reversed" ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200"}`} data-testid={`log-row-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">
                      {r.number} <span className="text-slate-400">·</span> <span className="text-slate-600">{r.reason}</span>
                      {r.kind === "reversal" && <span className="ml-1 text-xs text-emerald-700 font-semibold">REVERSAL</span>}
                      {r.status === "reversed" && <span className="ml-1 text-xs text-slate-500 font-semibold">REVERSED</span>}
                    </div>
                    <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()} · {r.created_by_name || "—"}</div>
                    <ul className="mt-1 text-xs text-slate-700 list-disc ml-4">
                      {(r.items || []).map((it, idx) => <li key={idx}>{it.product_name} × {Math.abs(it.qty)}{it.unit ? ` ${it.unit}` : ""}</li>)}
                    </ul>
                    {r.notes && <div className="text-xs text-slate-400 mt-1">{r.notes}</div>}
                  </div>
                  {canConsume && r.kind !== "reversal" && r.status === "recorded" && (
                    <Button variant="outline" size="sm" onClick={() => doReverse(r)} className="gap-1 shrink-0" data-testid={`log-reverse-${r.id}`}><RotateCcw className="h-3.5 w-3.5" /> Reverse</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
